// A piece of code on a dark blue box, with its own copy button: what the problem lists and the correction panel use
// for HTML, CSS selectors and XPath, so technical data always looks the same and is copied the same way.
import { CopyIcon } from '@radix-ui/react-icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Copies text to the clipboard, saying whether it worked; falls back to a hidden text area when the API is not there. */
export function useCopy() {
  const [status, setStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  async function copy(text: string) {
    let done = false;
    try {
      await navigator.clipboard.writeText(text);
      done = true;
    } catch {
      const area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.append(area);
      area.select();
      try {
        done = document.execCommand('copy');
      } catch {
        done = false;
      }
      area.remove();
    }
    setStatus(done ? 'copied' : 'failed');
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setStatus('idle'), 4000);
  }
  return { status, copy };
}

/**
 * A piece of code (HTML, a CSS selector, an XPath) on a dark blue box, in a monospaced face and apart from the text that
 * explains it, with its own copy button. Long lines scroll sideways inside the box: the panel never overflows.
 */
export function CodeBox({ code, label }: { code: string; label: string }) {
  const { t } = useTranslation();
  const { status, copy } = useCopy();
  return (
    <div className="code-box">
      <div className="code-box-head">
        <p className="code-box-label">{label}</p>
        <button type="button" className="code-box-copy" onClick={() => void copy(code)}>
          <CopyIcon aria-hidden="true" />
          {status === 'copied' ? t('element.proposal.copied') : t('element.proposal.copy')}
          <span className="sr-only"> — {label}</span>
        </button>
      </div>
      {/* It scrolls sideways: it must be reachable by keyboard to be read in full. */}
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling area must be reachable by keyboard. */}
      <pre tabIndex={0}>
        <code>{code}</code>
      </pre>
      <p role="status" className="sr-only">
        {status === 'copied'
          ? t('element.proposal.copied')
          : status === 'failed'
            ? t('element.proposal.failed')
            : ''}
      </p>
    </div>
  );
}
